import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import AppBar from '@material-ui/core/AppBar'
import IconButton from '@material-ui/core/IconButton'
import Badge from '@material-ui/core/Badge'
import Box from '@material-ui/core/Box'
import Toolbar from '@material-ui/core/Toolbar'
import MenuIcon from 'components/material-symbol/icons/Menu'
import MenuOpenIcon from 'components/material-symbol/icons/MenuOpen'
import LeftPanelCloseIcon from 'components/material-symbol/icons/LeftPanelClose'
import { useTheme } from '@material-ui/core/styles'
import { useLocation } from 'react-router-dom'
import AccountDropdown from 'components/account-dropdown'
import Footer from 'components/footer'
import BrandSwitcher from 'components/shell/brand-switcher'
import ContactButton from 'components/contact-button'
import Sidebar from './sidebar'
import useStyles from './styles'
import { getSidebarLayoutWidth, shouldShowNavLabels } from './sidebar-layout'
import { useIsCollapsibleSidebar, useIsDesktopNav } from 'lib/hooks'
import { getAssumedUser } from 'lib/auth/impersonation'
import clsx from 'clsx'

export default function AppShell({
  logo = { src: '', alt: '' },
  menuItems = [],
  showBrandSwitcher = true,
  showAccountDropdown = true,
  toolbarContent = null,
  unviewedLeadCount = null,
  showFooter = false,
  children,
}) {
  const assumedUser = getAssumedUser()
  const location = useLocation()
  const theme = useTheme()
  const isCollapsibleSidebar = useIsCollapsibleSidebar()
  const isDesktopNav = useIsDesktopNav()
  const isMobileNav = !isDesktopNav && !isCollapsibleSidebar

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const [isTabletNavOpen, setIsTabletNavOpen] = useState(true)

  const isNavCollapsed = isCollapsibleSidebar && !isTabletNavOpen
  const sidebarLayoutWidth = getSidebarLayoutWidth(theme, {
    isDesktopNav,
    isCollapsibleSidebar,
    isNavCollapsed,
  })
  const showNavLabels = shouldShowNavLabels({
    isDesktopNav,
    isMobileNav,
    isNavCollapsed,
  })
  const classes = useStyles({
    assumedUser,
    showFooter,
    sidebarLayoutWidth,
  })

  const handleDrawerToggle = () => {
    if (isMobileNav) {
      setIsMobileNavOpen(!isMobileNavOpen)
      return
    }
    if (isCollapsibleSidebar) {
      setIsTabletNavOpen(!isTabletNavOpen)
    }
  }

  useEffect(() => {
    if (isMobileNav) {
      setIsTabletNavOpen(true)
      return
    }
    if (isDesktopNav) {
      setIsTabletNavOpen(true)
      return
    }
    if (isCollapsibleSidebar) {
      setIsTabletNavOpen(false)
    }
  }, [isCollapsibleSidebar, isMobileNav, isDesktopNav])

  const drawerContainer =
    window !== undefined ? () => window.document.body : undefined

  return (
    <>
      {theme.palette.body?.background && (
        <Helmet>
          <style>{`body { background-color: ${theme.palette.body.background}; }`}</style>
        </Helmet>
      )}
      <Box display="flex">
        <AppBar position="fixed" square className={classes.appBar}>
          <Toolbar
            className={clsx(classes.toolbar, {
              [classes.toolbarCollapsibleSidebar]: isCollapsibleSidebar,
            })}
          >
            {menuItems.length > 0 && (
              <IconButton
                color="primary"
                aria-label="open drawer"
                edge={isMobileNav ? 'start' : false}
                onClick={handleDrawerToggle}
                className={
                  isMobileNav
                    ? classes.mobileMenuButton
                    : classes.tabletMenuButton
                }
              >
                <Badge
                  invisible={!location.pathname.startsWith('/pga-coach')}
                  classes={{
                    badge: clsx(
                      classes.mobileMenuButtonBadge,
                      unviewedLeadCount > 0 && classes.unviewedLeadBadge,
                    ),
                  }}
                  color={unviewedLeadCount > 0 ? 'default' : 'primary'}
                  variant="dot"
                  overlap="rectangular"
                >
                  {isMobileNav ? (
                    <MenuIcon color="primary" opticalSize={24} />
                  ) : !isTabletNavOpen ? (
                    <MenuOpenIcon color="primary" opticalSize={24} />
                  ) : (
                    <LeftPanelCloseIcon color="primary" opticalSize={24} />
                  )}
                </Badge>
              </IconButton>
            )}
            {toolbarContent || (
              <>
                {showBrandSwitcher ? (
                  <BrandSwitcher logo={logo} />
                ) : (
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    style={{
                      display: 'block',
                      width:
                        (isMobileNav
                          ? logo.mobileWidth ?? logo.width
                          : logo.width) ?? '110px',
                      height:
                        (isMobileNav
                          ? logo.mobileHeight ?? logo.height
                          : logo.height) ?? 'auto',
                    }}
                  />
                )}
                {showAccountDropdown && !isMobileNav && <Box flexGrow={1} />}
                {location.pathname.includes('messages') && <ContactButton />}
                {showAccountDropdown && <AccountDropdown />}
              </>
            )}
          </Toolbar>
        </AppBar>
        <Sidebar
          menuItems={menuItems}
          isMobileNavOpen={isMobileNavOpen}
          isTabletNavOpen={isTabletNavOpen}
          setIsTabletNavOpen={setIsTabletNavOpen}
          handleDrawerToggle={handleDrawerToggle}
          assumedUser={assumedUser}
          drawerContainer={drawerContainer}
          location={location}
          sidebarLayoutWidth={sidebarLayoutWidth}
          showNavLabels={showNavLabels}
          isNavCollapsed={isNavCollapsed}
        />
        <main className={classes.mainContent}>
          <div className={classes.toolbarSpacing} />
          {children}
          {showFooter && <Footer />}
        </main>
      </Box>
    </>
  )
}
